import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";

interface DeleteUserConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  userName: string;
  isPermanent?: boolean;
}

const RedButton = styled(Button)(({ theme }) => ({
  color: theme.palette.getContrastText("#f44336"),
  backgroundColor: "#f44336",
  "&:hover": {
    backgroundColor: "#d32f2f",
  },
}));

const DeleteUserConfirmationDialog: React.FC<DeleteUserConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  userName,
  isPermanent = false,
}) => {
  const [confirmationText, setConfirmationText] = useState("");
  const [isConfirmButtonEnabled, setIsConfirmButtonEnabled] = useState(false);

  const targetText = isPermanent ? "DELETE" : "ARCHIVE";

  useEffect(() => {
    setIsConfirmButtonEnabled(confirmationText === targetText);
  }, [confirmationText, targetText]);

  useEffect(() => {
    if (!open) {
      setConfirmationText("");
    }
  }, [open]);

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmationText(event.target.value);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="delete-user-dialog-title"
      aria-describedby="delete-user-dialog-description"
      PaperProps={{
        style: {
          backgroundColor: "#1C1A26", // Dark background for the dialog
          color: "white", // White text
        },
      }}
    >
      <DialogTitle id="delete-user-dialog-title" sx={{ color: "white" }}>
        {isPermanent ? "Confirm Permanent User Deletion" : "Confirm User Archiving"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText
          id="delete-user-dialog-description"
          sx={{ color: "lightgray", marginBottom: 2 }}
        >
          {isPermanent ? (
            <>
              You are about to permanently delete the user "
              <span style={{ fontWeight: "bold", color: "white" }}>
                {userName}
              </span>
              ". This user will be completely removed from your organisation. This is a destructive action and cannot be undone.
            </>
          ) : (
            <>
              You are about to archive the user "
              <span style={{ fontWeight: "bold", color: "white" }}>
                {userName}
              </span>
              ". Once archived, they cannot perform any task, use any feature, or log in to the platform at all.
            </>
          )}
          <br />
          <br />
          To confirm, please type "
          <span style={{ fontWeight: "bold", color: "white" }}>{targetText}</span>"
          in the box below.
        </DialogContentText>
        <TextField
          autoFocus
          margin="dense"
          id="confirmation"
          label={`Type "${targetText}" to confirm`}
          type="text"
          fullWidth
          variant="outlined"
          value={confirmationText}
          onChange={handleTextChange}
          InputLabelProps={{
            style: { color: "lightgray" },
          }}
          InputProps={{
            style: { color: "white" },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#313131",
              },
              "&:hover fieldset": {
                borderColor: "#B8F890",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#B8F890",
              },
            },
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: "white" }}>
          Cancel
        </Button>
        <RedButton
          onClick={onConfirm}
          disabled={!isConfirmButtonEnabled}
          variant="contained"
        >
          {isPermanent ? "Delete" : "Archive"}
        </RedButton>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteUserConfirmationDialog;
